// Brightness / Contrast — Photoshop's "Use Legacy" off algorithm.
//
// Algorithm choice for Brightness (non-legacy "soft" modern formulation):
//
//   For brightness b in [-150, +150] applied to channel value v in [0, 255]:
//
//     if b >= 0:
//       v_out = v + b * (1 - v/255)     -> dark pixels lifted more than light
//     else:
//       v_out = v + b * (v/255)         -> light pixels crushed more than dark
//
// This is a well-known and widely used "monotone, endpoint-preserving" model
// of Photoshop's modern Brightness slider: 0 is identity, +150 cannot push a
// value past 255 (because the multiplier (1 - v/255) is 0 at v=255), and
// -150 cannot push a value below 0 (because v/255 is 0 at v=0). Mid-tones
// move the most. It matches Photoshop's qualitative behavior — increasing
// brightness lifts shadows and midtones without blowing out highlights, and
// decreasing brightness crushes highlights and midtones without crushing
// pure-black shadows. The exact pixel values don't bit-match Photoshop (the
// "real" algorithm is undocumented and changes between versions), but the
// curve shape and endpoint preservation do.
//
// Contrast is the standard Photoshop formulation:
//
//   v_out = (v - 128) * (1 + c/100) + 128
//
// for c in [-50, +100]. Both stages are baked into a single 256-entry LUT
// (brightness then contrast — order matters; we apply brightness first, then
// contrast, matching Photoshop's stage order).
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface BrightnessContrastParams {
  /** -150..+150. 0 = no change. */
  brightness: number;
  /** -50..+100. 0 = no change. */
  contrast: number;
}

const DEFAULT_PARAMS: BrightnessContrastParams = {
  brightness: 0,
  contrast: 0,
};

/**
 * Build a 256-entry LUT for the combined brightness/contrast mapping.
 *
 * Exported for testing.
 */
export function buildBrightnessContrastLut(
  params: BrightnessContrastParams,
): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  const b = clampRange(params.brightness, -150, 150);
  const c = clampRange(params.contrast, -50, 100);
  const contrastFactor = 1 + c / 100;

  for (let v = 0; v < 256; v++) {
    // Brightness stage.
    let out: number;
    if (b === 0) {
      out = v;
    } else if (b > 0) {
      out = v + b * (1 - v / 255);
    } else {
      out = v + b * (v / 255);
    }
    // Contrast stage.
    if (c !== 0) {
      out = (out - 128) * contrastFactor + 128;
    }
    // Uint8ClampedArray rounds + clamps on assignment.
    lut[v] = Math.round(out);
  }
  return lut;
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

export const BrightnessContrast: Filter<BrightnessContrastParams> = {
  id: "brightness-contrast",
  displayName: "Brightness/Contrast",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: BrightnessContrastParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const lut = buildBrightnessContrastLut(params);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = lut[src[o] ?? 0] ?? 0;
      dst[o + 1] = lut[src[o + 1] ?? 0] ?? 0;
      dst[o + 2] = lut[src[o + 2] ?? 0] ?? 0;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};
